import React, { useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import PersonIcon from '@mui/icons-material/Person'
import GraphicEqIcon from '@mui/icons-material/GraphicEq'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import {
  TtsVoice,
  VOCAL_RANGE_DESC,
  availableRanges as deriveRanges,
  availableGenders as deriveGenders,
  filterVoices,
  voiceMatchesRange,
} from '../../types/tts'
import { useAudioPreview } from '../../hooks/useAudioPreview'
import { tokens } from '../../theme/tokens'

interface VoicePickerProps {
  /** All stock voices for the chosen language (drives the filter chips). */
  voices: TtsVoice[]
  /** The subset visible after the parent's gender/range filters (what we render). */
  filteredVoices: TtsVoice[]
  selectedVoiceId: string
  onSelectVoice: (voiceId: string) => void
  /** 'all' | 'female' | 'male' — lifted to the parent so it can reconcile the
   *  selection to a visible voice. */
  genderFilter: string
  /** Active range filter, or '' for none. Lifted to the parent. */
  rangeFilter: string
  onToggleGender: (value: string) => void
  onToggleRange: (value: string) => void
  loading?: boolean
}

/**
 * Voice list for a chosen language (catalog v4). Each voice leads with a human
 * NAME, gender and vocal RANGE (with a human descriptor), an optional f0
 * indicator, and a preview button. Gender/range filters are toggle chips whose
 * state is OWNED BY THE PARENT (TtsPlayground) — the parent renders only the
 * visible subset and keeps the selection on a visible voice. Source corpus is
 * never shown.
 *
 * The chip rows are derived from the language's UNFILTERED voices, but each chip
 * is disabled when the OTHER active filter leaves it with nothing to match, so
 * the two filters can never be combined into an empty list.
 */
const VoicePicker: React.FC<VoicePickerProps> = ({
  voices,
  filteredVoices,
  selectedVoiceId,
  onSelectVoice,
  genderFilter,
  rangeFilter,
  onToggleGender,
  onToggleRange,
  loading = false,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  // Shared preview state machine (200ms fade-out, self-clearing highlight). Keyed
  // on the voice set so switching language stops playback and drops the highlight.
  const preview = useAudioPreview(voices)

  // Both chip rows are derived from THIS LANGUAGE's voices (shared, unit-tested
  // helpers), so every chip offered matches at least one voice. A row with only
  // one value is dropped: filtering on it would be identical to not filtering.
  const availableRanges = useMemo(() => deriveRanges(voices), [voices])
  const availableGenders = useMemo(() => deriveGenders(voices), [voices])
  const showRanges = availableRanges.length > 1
  const showGenders = availableGenders.length > 1
  const genderLabels: Record<string, string> = {
    female: t('tts.genderF', 'Female'),
    male: t('tts.genderM', 'Male'),
  }

  // The two filters must stay MUTUALLY CONSISTENT: a chip that would empty the
  // list given the other active filter is shown but disabled, so combinations
  // like Spanish + Female + Tenor (both chips real, no voice is both) can't be
  // assembled. Disabled rather than hidden — the user still sees the range
  // exists, it's just not available for the gender they picked. The ACTIVE chip
  // is never disabled: it has to stay clickable to be cleared.
  const enabledRanges = useMemo(() => {
    const withGender = filterVoices(voices, { gender: genderFilter })
    return new Set(availableRanges.filter((r) => withGender.some((v) => voiceMatchesRange(v, r))))
  }, [voices, genderFilter, availableRanges])

  const enabledGenders = useMemo(() => {
    const withRange = filterVoices(voices, { range: rangeFilter })
    return new Set(availableGenders.filter((g) => withRange.some((v) => v.gender === g)))
  }, [voices, rangeFilter, availableGenders])

  const filterChipSx = (active: boolean, disabled: boolean) => ({
    height: 28,
    borderRadius: 1.5,
    fontWeight: 600,
    fontSize: '0.75rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    textTransform: 'capitalize' as const,
    ...(active
      ? {
          backgroundColor: isDark ? `${tokens.colors.tts.main}40` : `${tokens.colors.tts.main}15`,
          color: isDark ? tokens.colors.tts.light : tokens.colors.tts.main,
          borderColor: isDark ? tokens.colors.tts.light : tokens.colors.tts.main,
        }
      : {
          backgroundColor: 'transparent',
          color: isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary',
          borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
        }),
    ...(disabled ? { opacity: 0.38 } : {}),
  })

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 700,
          color: isDark ? 'rgba(255,255,255,0.9)' : tokens.colors.tts.main,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.7rem',
          mb: 1.5,
        }}
      >
        {t('tts.chooseVoice', 'Choose a voice')}
      </Typography>

      {/* Filter chips — gender and vocal range, both plain toggles: clicking the
          active chip clears it. */}
      {(showGenders || showRanges) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
          {showGenders &&
            availableGenders.map((g) => {
              const active = genderFilter === g
              const disabled = !active && !enabledGenders.has(g)
              return (
                <Chip
                  key={g}
                  label={genderLabels[g] ?? g}
                  variant="outlined"
                  onClick={disabled ? undefined : () => onToggleGender(g)}
                  aria-pressed={active}
                  aria-disabled={disabled}
                  disabled={disabled}
                  sx={filterChipSx(active, disabled)}
                />
              )
            })}
          {showRanges &&
            availableRanges.map((r) => {
              const active = rangeFilter === r
              const disabled = !active && !enabledRanges.has(r)
              return (
                <Chip
                  key={r}
                  label={r}
                  variant="outlined"
                  onClick={disabled ? undefined : () => onToggleRange(r)}
                  aria-pressed={active}
                  aria-disabled={disabled}
                  disabled={disabled}
                  sx={filterChipSx(active, disabled)}
                />
              )
            })}
        </Box>
      )}

      {loading && (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
          {t('tts.loadingVoices', 'Loading voices...')}
        </Typography>
      )}

      {!loading && voices.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
          {t('tts.noVoicesForLanguage', 'No voices available for this language yet')}
        </Typography>
      )}

      {!loading && voices.length > 0 && filteredVoices.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
          {t('tts.noVoicesMatch', 'No voices match these filters')}
        </Typography>
      )}

      {/* Voice list */}
      <Box
        role="listbox"
        aria-label={t('tts.chooseVoice', 'Choose a voice')}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {filteredVoices.map((voice) => {
          const selected = voice.voice_id === selectedVoiceId
          const isPlaying = preview.playingId === voice.voice_id
          const rangeDesc = voice.range ? VOCAL_RANGE_DESC[voice.range] : undefined
          return (
            <Box
              key={voice.voice_id}
              role="option"
              aria-selected={selected}
              tabIndex={0}
              onClick={() => onSelectVoice(voice.voice_id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSelectVoice(voice.voice_id)
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                border: '1px solid',
                transition: 'all 0.15s ease',
                borderColor: selected
                  ? (isDark ? tokens.colors.tts.light : tokens.colors.tts.main)
                  : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
                background: selected
                  ? (isDark
                      ? `linear-gradient(135deg, ${tokens.colors.tts.main}40 0%, ${tokens.colors.tts.light}20 100%)`
                      : `linear-gradient(135deg, ${tokens.colors.tts.surface} 0%, rgba(0,117,138,0.08) 100%)`)
                  : (isDark ? 'rgba(255,255,255,0.03)' : 'background.paper'),
                '&:hover': {
                  borderColor: isDark ? tokens.colors.tts.light : tokens.colors.tts.main,
                },
              }}
            >
              {/* Preview button */}
              <Tooltip title={isPlaying ? t('stopPreview', 'Stop') : t('previewVoice', 'Preview')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      preview.toggle(voice.voice_id, voice.preview_url)
                    }}
                    disabled={!voice.preview_url}
                    aria-label={isPlaying ? t('stopPreview', 'Stop') : t('previewVoice', 'Preview')}
                    sx={{
                      color: isPlaying
                        ? (isDark ? tokens.colors.tts.light : tokens.colors.tts.main)
                        : undefined,
                    }}
                  >
                    {isPlaying ? <StopIcon /> : <PlayArrowIcon />}
                  </IconButton>
                </span>
              </Tooltip>

              {/* Name + meta */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body1"
                  noWrap
                  sx={{
                    fontWeight: 700,
                    color: selected
                      ? (isDark ? '#fff' : tokens.colors.tts.main)
                      : (isDark ? 'rgba(255,255,255,0.9)' : 'text.primary'),
                  }}
                >
                  {voice.displayName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.25 }}>
                  {voice.gender && voice.gender !== 'unknown' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                      <PersonIcon sx={{ fontSize: 13, color: isDark ? 'rgba(255,255,255,0.5)' : 'text.secondary' }} />
                      <Typography
                        variant="caption"
                        sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary', textTransform: 'capitalize' }}
                      >
                        {voice.gender}
                      </Typography>
                    </Box>
                  )}
                  {voice.range && (
                    <Typography
                      variant="caption"
                      sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary' }}
                    >
                      <Box component="span" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                        {voice.range}
                      </Box>
                      {rangeDesc ? ` · ${rangeDesc}` : ''}
                    </Typography>
                  )}
                  {typeof voice.f0 === 'number' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                      <GraphicEqIcon sx={{ fontSize: 13, color: isDark ? 'rgba(255,255,255,0.5)' : 'text.secondary' }} />
                      <Typography
                        variant="caption"
                        sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary' }}
                      >
                        {Math.round(voice.f0)} Hz
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {selected && (
                <Chip
                  label={t('tts.selected', 'Selected')}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    background: tokens.gradients.tts,
                    color: '#fff',
                  }}
                />
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default VoicePicker
